import { useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useOrganizationFolders } from '../hooks/useOrganizationFolders';
import FolderShareModal from './FolderShareModal';
import type { OrganizationFolder } from '../types';

interface TeamFoldersSectionProps {
  organizationId: string;
  ownerEmail: string;
  memberEmails: string[];
}

/**
 * Owner/admin-facing folder management for a team, surfaced on the Teams page
 * next to the usage chart. Lists every folder and opens FolderShareModal (which
 * owns rename, sharing/permissions, and delete) for create and manage actions.
 */
const TeamFoldersSection = ({ organizationId, ownerEmail, memberEmails }: TeamFoldersSectionProps) => {
  const { folders, isLoading, refetch } = useOrganizationFolders(organizationId);
  const [modalOpen, setModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<OrganizationFolder | undefined>(undefined);

  const openCreate = () => {
    setFolderToEdit(undefined);
    setModalOpen(true);
  };

  const openManage = (folder: OrganizationFolder) => {
    setFolderToEdit(folder);
    setModalOpen(true);
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center" gap={1}>
          <FolderOutlinedIcon fontSize="small" />
          Team Folders
        </Typography>
        <Button size="small" startIcon={<CreateNewFolderOutlinedIcon />} onClick={openCreate}>
          New folder
        </Button>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={24} />
        </Stack>
      ) : folders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No folders yet. Create one to organize the team&apos;s notes and control who can see them.
        </Typography>
      ) : (
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {folders.map((folder) => (
            <Stack key={folder.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1, minWidth: 0 }}>
              <FolderOutlinedIcon fontSize="small" color="action" />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {folder.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {folder.note_ids.length} note{folder.note_ids.length !== 1 ? 's' : ''}
                </Typography>
              </Stack>
              <Chip
                size="small"
                variant="outlined"
                label={
                  folder.access === 'team'
                    ? 'Entire team'
                    : `${folder.member_emails.length} member${folder.member_emails.length !== 1 ? 's' : ''}`
                }
              />
              <IconButton size="small" aria-label={`Manage ${folder.name}`} onClick={() => openManage(folder)}>
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <FolderShareModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        organizationId={organizationId}
        memberEmails={memberEmails}
        ownerEmail={ownerEmail}
        folder={folderToEdit}
        onSaved={() => refetch()}
      />
    </Box>
  );
};

export default TeamFoldersSection;
